import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useAppContext } from "../../../../AppContext";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { UnitOfMeasure } from "../../../../common/enums";
import {
  IInventoryCategoryModel,
} from "../../../nomenclatures/inventory/models/inventoryNomenclatureModels";
import { IInventoryItemSearchResultModel } from "../models/inventoryTransferModels";

/**
 * Minimal form model for the "Quick add product" dialog stacked on top of
 * the transfer dialog. InitialQuantity is hardcoded to 0 (the transfer
 * itself brings the stock) and cannot be changed here.
 */
export interface IQuickAddProductFormModel {
  categoryId: number | null;
  name: string;
  brand: string | null;
  barcode: string | null;
  price: number;
  costPrice: number;
  unitOfMeasure: UnitOfMeasure;
  lowStockThreshold: number;
}

export const quickAddProductSchema = yup
  .object({
    categoryId: yup
      .number()
      .typeError("Изберете категория")
      .required("Изберете категория")
      .min(1, "Изберете категория")
      .defined(),
    name: yup.string().required("Името е задължително").defined(),
    brand: yup.string().nullable().defined(),
    barcode: yup.string().nullable().defined(),
    price: yup
      .number()
      .typeError("Цената е задължителна")
      .min(0)
      .required()
      .defined(),
    costPrice: yup
      .number()
      .typeError("Себестойността е задължителна")
      .min(0)
      .required()
      .defined(),
    unitOfMeasure: yup
      .number()
      .oneOf([UnitOfMeasure.Piece, UnitOfMeasure.Gram])
      .required()
      .defined(),
    lowStockThreshold: yup
      .number()
      .typeError("Прагът е задължителен")
      .min(0)
      .required()
      .defined(),
  })
  .required();

const defaults = (): IQuickAddProductFormModel => ({
  categoryId: null,
  name: "",
  brand: "",
  barcode: "",
  price: 0,
  costPrice: 0,
  unitOfMeasure: UnitOfMeasure.Piece,
  lowStockThreshold: 5,
});

interface IQuickAddArgs {
  /** Called with the newly-created product so the parent can append it as a line. */
  onCreated: (product: IInventoryItemSearchResultModel) => void;
}

export const useTransferQuickAddProduct = ({ onCreated }: IQuickAddArgs) => {
  const { selectedStudioId } = useAppContext();
  const endpoints = endpointsMetadata.inventoryItemNomenclature;
  const queryClient = useQueryClient();

  const { data: categories = [] } = useApiQuery<IInventoryCategoryModel[]>({
    queryKey: [endpoints.getAllCategories, selectedStudioId],
    endpoint: endpoints.getAllCategories,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  const form = useForm<IQuickAddProductFormModel>({
    resolver: yupResolver(quickAddProductSchema) as any,
    defaultValues: defaults(),
    mode: "onBlur",
  });

  const createMutation = useApiMutation<string>({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [endpoints.getAllCategorized, selectedStudioId],
      });
    },
  });

  const resetForm = () => form.reset(defaults());

  const submit = form.handleSubmit(async (values) => {
    // The backend Create endpoint returns a success string, not the created
    // entity. After a successful create we search for the product by barcode
    // (if provided) or by exact name to resolve its id, emoji and uom so we
    // can hand it back to the parent for line insertion.
    await new Promise<void>((resolve) => {
      createMutation.mutate(
        {
          endpoint: endpoints.create,
          method: "post",
          payload: {
            name: values.name,
            brand: values.brand?.trim() || null,
            sku: null,
            barcode: values.barcode?.trim() || null,
            description: null,
            price: values.price,
            costPrice: values.costPrice,
            unitOfMeasure: values.unitOfMeasure,
            // InitialQuantity is hardcoded to 0 for the quick-add flow —
            // the transfer is expected to bring the stock.
            initialQuantity: 0,
            lowStockThreshold: values.lowStockThreshold,
            categoryId: values.categoryId,
          },
        },
        {
          onSuccess: async () => {
            try {
              const lookupQuery =
                (values.barcode && values.barcode.trim().length > 0
                  ? values.barcode.trim()
                  : values.name.trim()) ?? "";

              const { default: axiosInstance } = await import(
                "../../../../common/infrastructure/helpers/axiosHelper"
              );
              const response = await axiosInstance.get<IInventoryItemSearchResultModel[]>(
                `${endpoints.searchProducts}?query=${encodeURIComponent(lookupQuery)}`,
              );
              const matches = response.data ?? [];
              const created =
                matches.find(
                  (m) =>
                    (values.barcode && m.barcode === values.barcode.trim()) ||
                    m.name === values.name.trim(),
                ) ?? matches[0];

              if (created) {
                onCreated(created);
                resetForm();
              }
            } finally {
              resolve();
            }
          },
          onError: () => resolve(),
        },
      );
    });
  });

  return {
    form,
    categories: categories.filter((c) => !c.isDeleted),
    submit,
    isSubmitting: createMutation.isLoading,
    resetForm,
  };
};
